import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Button,
} from '@ipc/ipc-components';
import {
  getColumns,
  getData,
  getIsLoading,
  getElementTypeList,
  getIsElementTypeListLoaded,
  getEditMode,
  getEditChanges,
} from '../redux/selectors/NotificationServiceThresholdSelectors';
import {
  createThresholdRequestActionCreator,
  deleteThresholdRequestActionCreator,
  getElementTypesRequestActionCreator,
  editThresholdRequestActionCreator,
} from '../redux/actions/NotificationServiceActions';
import { clearTableCustomFilter } from '../../../../common/list-table/actions/ListTableActions';
import { blockRoutingAction, triggerConfirmationDialogAction, triggerRedirect } from '../../../../common/redux/actions/redirectActions';
import { getUserRole } from '../../../../common/user/redux/selectors/UserSelectors';
import TableDefinitions from '../../../../common/TableDefinitions';
import ListTable from '../../../../common/list-table/components/ListTable';
import { NotificationServiceThresholdStyles } from '../component/NotificationServiceThreshold.styles';
import CreateThresholdDialog from '../create-threshold/component/CreateThresholdDialog';
import NotificationServiceThresholdColumns, { editColumns } from './NotificationServiceThresholdColumns';
import { formatMessage } from '../../../../../locales/locales';
import ConfirmationDialog from '../../../../common/list-table/helpers/confirmationDialog';
import { getShowConfirmationDialog } from '../../../../common/redux/selectors/redirectSelector';

function NotificationServiceThresholdContainer(props) {
  const {
    isLoading,
    data,
    getElementTypeListRequest,
    isElementTypeListLoaded,
    elementTypeList,
    createThreshold,
    deleteThreshold,
    userRole,
    editThreshold,
    setRouteBlocker,
    showConfirmationDialog,
    updateConfirmationDialog,
    confirmRedirect,
    openEditMode,
    openEditModeChanges,
    clearFilteredData,
  } = props;

  useEffect(() => {
    if (!isElementTypeListLoaded && elementTypeList.length === 0) {
      getElementTypeListRequest();
    }
  }, []);
  const [createVisible, setCreateVisible] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editMode, switchEditMode] = useState(false);
  const [localChanges, setLocalChanges] = useState([]);
  const [deleteVisible, updateDeleteVisibility] = useState(false);
  const [visibleData, setVisibleData] = useState(data);
  const [isChangesValid, updateChangesValidity] = useState(true);

  const createThresholdHandler = (requestParams) => {
    createThreshold(requestParams);
  };
  const openNewThresholdDialog = () => {
    setCreateVisible(true);
  };
  useEffect(() => {
    if (openEditModeChanges) {
      const editData = data.map((record) => {
        const requestData = openEditModeChanges.find((item) => item.thresholdId === record.id);
        if (requestData) {
          return {
            ...record,
            value: Number(requestData.value),
            contactGroup: requestData.contactGroupCode,
          };
        }
        return record;
      });
      setVisibleData(editData);
    } else {
      setVisibleData(data);
    }
  }, [data]);

  useEffect(() => {
    setRouteBlocker(editMode);
    if (editMode) {
      clearFilteredData(
        { listId: TableDefinitions.NOTIFICATION_SERVICE_THRESHOLD.id },
      );
    }
  }, [editMode]);

  useEffect(() => {
    const changes = visibleData.filter((item) => {
      const defaultItem = data.find((element) => element.id === item.id);
      return !!(defaultItem && (Number(defaultItem.value) !== Number(item.value) || defaultItem.contactGroup !== item.contactGroup));
    });
    setLocalChanges(changes);
    const invalidRecords = visibleData.filter((item) => !item.value);
    updateChangesValidity(invalidRecords.length === 0);
  }, [visibleData]);

  useEffect(() => {
    if (openEditMode) {
      if (openEditModeChanges) {
        const editData = data.map((record) => {
          const requestData = openEditModeChanges.find((item) => item.thresholdId === record.id);
          if (requestData) {
            return {
              ...record,
              value: Number(requestData.value),
              contactGroup: requestData.contactGroupCode,
            };
          }
          return record;
        });
        setVisibleData(editData);
      }
      switchEditMode(true);
    }
  }, [openEditMode]);

  const handleDeleteDialogCancel = () => {
    updateDeleteVisibility(false);
  };
  const handleEditModeCancel = () => {
    setVisibleData(data);
    switchEditMode(false);
  };
  const handleEditModeSave = async () => {
    const requestData = localChanges.map((item) => ({
      contactGroupCode: item.contactGroup,
      elementId: elementTypeList.find((element) => element.name === item.name).id,
      thresholdId: item.id,
      value: item.value,
    }));
    await editThreshold(requestData);
  };
  const onRowDelete = (row) => {
    setRowToDelete(row.id);
    updateDeleteVisibility(true);
  };

  const handleConfirmDelete = async () => {
    await deleteThreshold({
      thresholdId: rowToDelete,
    });
    updateDeleteVisibility(false);
  };

  const cancelHandler = () => {
    setRouteBlocker(false);
    if (showConfirmationDialog) {
      updateConfirmationDialog(false);
      confirmRedirect(true);
    }
  };

  const columns = NotificationServiceThresholdColumns(data, userRole, onRowDelete);

  return (
    <NotificationServiceThresholdStyles>
      <div className="mrd-contact-button-container">
        { userRole.includes('IPC_ADMIN') && !editMode && (
          <Button
            onClick={openNewThresholdDialog}
            className={`primary-button-${editMode} mrd-contact-button`}
            disabled={editMode}
          >
            New threshold
          </Button>
        )}
        { userRole.includes('IPC_ADMIN') && !editMode && (
          <Button
            onClick={() => switchEditMode(!editMode)}
            className={`primary-button-${editMode} mrd-contact-button`}
            disabled={editMode}
          >
            Edit
          </Button>
        )}
        { userRole.includes('IPC_ADMIN') && editMode && (
          <Button
            onClick={handleEditModeSave}
            className={`primary-button-${localChanges.length === 0 || !isChangesValid} mrd-contact-button`}
            disabled={localChanges.length === 0 || !isChangesValid}
          >
            Save
          </Button>
        )}
        { userRole.includes('IPC_ADMIN') && editMode && (
          <Button
            onClick={handleEditModeCancel}
            className="primary-button-false mrd-contact-button"
          >
            Cancel
          </Button>
        )}
      </div>
      <ListTable
        listId={TableDefinitions.NOTIFICATION_SERVICE_THRESHOLD.id}
        loading={isLoading}
        columns={editMode ? editColumns(visibleData, setVisibleData) : columns}
        data={visibleData}
      />
      <ConfirmationDialog
        isOpen={deleteVisible}
        submitButtonText="Yes"
        cancelButtonText="No"
        onClose={handleDeleteDialogCancel}
        onSubmit={handleConfirmDelete}
        confirmationTitle={formatMessage('en', 'threshold.delete.active.label')}
      />
      <ConfirmationDialog
        isOpen={showConfirmationDialog}
        submitButtonText="Yes"
        cancelButtonText="No"
        onClose={() => updateConfirmationDialog(false)}
        onSubmit={cancelHandler}
        confirmationTitle={formatMessage('en', 'impcmapping.deactive.unsaved.changes.warning.message')}
      />
      { createVisible && (
        <CreateThresholdDialog
          isOpen={createVisible}
          closeDialog={() => { setCreateVisible(false); }}
          onSave={createThresholdHandler}
          elementList={elementTypeList}
          dataList={data}
        />
      )}
    </NotificationServiceThresholdStyles>
  );
}

function mapStateToProps(state) {
  return {
    data: getData(state),
    columns: getColumns(state),
    isLoading: getIsLoading(state),
    elementTypeList: getElementTypeList(state),
    isElementTypeListLoaded: getIsElementTypeListLoaded(state),
    showConfirmationDialog: getShowConfirmationDialog(state),
    openEditMode: getEditMode(state),
    openEditModeChanges: getEditChanges(state),
    userRole: getUserRole(state),
  };
}

NotificationServiceThresholdContainer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  elementTypeList: PropTypes.arrayOf(PropTypes.any),
  isElementTypeListLoaded: PropTypes.bool,
  getElementTypeListRequest: PropTypes.func,
  createThreshold: PropTypes.func,
  userRole: PropTypes.string,
  deleteThreshold: PropTypes.func,
  editThreshold: PropTypes.func,
  setRouteBlocker: PropTypes.func,
  showConfirmationDialog: PropTypes.bool,
  updateConfirmationDialog: PropTypes.func,
  confirmRedirect: PropTypes.func,
  openEditMode: PropTypes.bool,
  openEditModeChanges: PropTypes.arrayOf(PropTypes.object),
  clearFilteredData: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  deleteThreshold: deleteThresholdRequestActionCreator,
  getElementTypeListRequest: getElementTypesRequestActionCreator,
  createThreshold: createThresholdRequestActionCreator,
  editThreshold: editThresholdRequestActionCreator,
  setRouteBlocker: blockRoutingAction,
  updateConfirmationDialog: triggerConfirmationDialogAction,
  confirmRedirect: triggerRedirect,
  clearFilteredData: clearTableCustomFilter,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(NotificationServiceThresholdContainer);
